/**
 * Types and interfaces for arrow calculators
 */

import type { FormatOptions } from "@alchemiesolns/svg-format";
import type { Point } from "../types";

/**
 * Result from arrow endpoint calculation
 */
export interface ArrowEndpoints {
  /** Starting position of the arrow curve */
  startPos: number[];
  /** Ending position of the arrow curve */
  endPos: number[];
  /** Final endpoint (may differ from endPos for bond targets) */
  p3: number[];
  /** Direction vector at arrow start */
  startDir: number[];
  /** Direction vector at arrow end */
  endDir: number[];
  /** Whether destination is adjacent to source */
  isDestAdjacent: boolean;
}

/**
 * Configuration for arrow calculation
 */
export interface ArrowCalculationConfig {
  source: Point;
  destination: Point;
  cameraDir: number[];
  isDestAdjacent: boolean;
  type: string;
  format: FormatOptions;
  overrideDest?: Point;
}

/**
 * Base interface for arrow type calculators
 *
 * Each arrow type (PA, PB, BA, BB) implements this interface
 * to provide type-specific calculation logic.
 */
export interface IArrowCalculator {
  /**
   * Calculate arrow endpoints and directions for this arrow type
   */
  calculate(config: ArrowCalculationConfig): ArrowEndpoints;

  /**
   * Get a human-readable name for this calculator type
   */
  getTypeName(): string;
}

/**
 * Abstract base class for arrow calculators
 *
 * Provides common functionality shared across all arrow types
 */
export abstract class ArrowCalculator implements IArrowCalculator {
  abstract calculate(config: ArrowCalculationConfig): ArrowEndpoints;
  abstract getTypeName(): string;

  /**
   * Handle fish arrow type endpoint modification
   * (shortens arrow to midpoint if not adjacent)
   */
  protected handleFishArrow(
    config: ArrowCalculationConfig,
    startPos: number[],
    endPos: number[]
  ): { endPos: number[]; destOffset: number } {
    if (config.type === "fish" && !config.isDestAdjacent) {
      const midpoint = [
        (startPos[0] + endPos[0]) / 2,
        (startPos[1] + endPos[1]) / 2,
        (startPos[2] + endPos[2]) / 2,
      ];
      return {
        endPos: midpoint,
        destOffset: config.format.atom.radius,
      };
    }
    return { endPos, destOffset: config.destination.offset };
  }

  /**
   * Common pattern: blend two directions and normalize
   */
  protected blendAndNormalize(
    dir1: number[],
    dir2: number[],
    weight: number = 0.5
  ): number[] {
    return [
      dir1[0] + dir2[0] * weight,
      dir1[1] + dir2[1] * weight,
      dir1[2] + dir2[2] * weight,
    ].map((v) => v / Math.sqrt(dir1[0] ** 2 + dir1[1] ** 2 + dir1[2] ** 2)) as number[];
  }
}
